export const SITE_URL = "https://jiia-studio.com";

export interface RouteMetaInput {
  /** Path starting with "/", e.g. "/prestations" */
  path: string;
  title: string;
  description: string;
  /** "website" | "article" | "profile" ... */
  type?: string;
  /** Absolute or root-relative image URL */
  image?: string | null | undefined;
  /** One or more JSON-LD objects to render as <script type="application/ld+json"> in <head> */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | undefined;
}

const absolute = (url: string) => (url.startsWith("http") ? url : `${SITE_URL}${url}`);

/**
 * Builds per-route head metadata: unique title/description, self-referencing
 * canonical + og:url, og:type (article on blog posts / project pages), and
 * optional JSON-LD structured data rendered server-side.
 */
export function routeHead({ path, title, description, type = "website", image, jsonLd }: RouteMetaInput) {
  const url = absolute(path);
  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];
  if (image) {
    const img = absolute(image);
    meta.push({ property: "og:image", content: img });
    meta.push({ name: "twitter:image", content: img });
  }
  const head: { meta: Array<Record<string, string>>; links: Array<Record<string, string>>; scripts?: Array<Record<string, string>> } = {
    meta,
    links: [{ rel: "canonical", href: url }],
  };
  if (jsonLd) {
    const data = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    head.scripts = data.map((d) => ({
      type: "application/ld+json",
      children: JSON.stringify(d),
    }));
  }
  return head;
}
